import posthog, { DisplaySurveyType, SurveyPosition } from "posthog-js";
import { configValue } from "@/services/config";
import type { CompanyInformation } from "@/core/api/data-contracts";

// ponytail: hardcoded while we evaluate PostHog. The project key is public (write-only, ships to the browser).
// Move to Key Vault (VUE_APP_POSTHOG_KEY) if we keep it and want per-environment projects or a kill switch.
const POSTHOG_KEY = "phc_osNY4wejKqWoG3Y8tAGLocZ5qVLruWcWxUG8uZ6pLofN";
const POSTHOG_HOST = "https://eu.i.posthog.com";

const EXPENSE_FEEDBACK_SURVEY_V2_ID = "01a17c9c-d0ef-0000-8601-ea9375f9929a";

// "kAI – feedback" survey, same API presentation
const KAI_FEEDBACK_SURVEY_ID = "01a1581b-a6fa-0000-257c-7ebccd87e032";

const EXPENSE_NEWS_SURVEY_ID = "01a17c44-8515-0000-7a9c-4a121f59eb2c";

// "Ärenden 2.0 – feedback" survey, same API presentation
const DRAFT_FEEDBACK_SURVEY_ID = "01a1878a-12d8-0000-93e3-8388a8deb142";

export function isPostHogEnabled() {
  return posthog.__loaded;
}

/**
 * A one-question API survey whose question the app draws itself (the news tour's thumbs). PostHog only
 * stores the answers: these events are what its own survey popover would send, so the survey's results
 * page counts them, and the person properties keep its "once" targeting right.
 */
export type InlineSurvey = { id: string; questionId: string; question: string };

export function captureSurveyShown(survey: InlineSurvey, extra: Record<string, unknown> = {}) {
  if (isPostHogEnabled()) {
    posthog.capture("survey shown", { $survey_id: survey.id, ...extra });
  }
}

export function captureSurveyAnswer(survey: InlineSurvey, response: string, extra: Record<string, unknown> = {}) {
  if (isPostHogEnabled()) {
    posthog.capture("survey sent", {
      $survey_id: survey.id,
      $survey_questions: [{ id: survey.questionId, question: survey.question, response }],
      [`$survey_response_${survey.questionId}`]: response,
      $survey_completed: true,
      $set: { [`$survey_responded/${survey.id}`]: true },
      ...extra,
    });
  }
}

export function captureSurveyDismissed(survey: InlineSurvey, extra: Record<string, unknown> = {}) {
  if (isPostHogEnabled()) {
    posthog.capture("survey dismissed", {
      $survey_id: survey.id,
      $survey_partially_completed: false,
      $set: { [`$survey_dismissed/${survey.id}`]: true },
      ...extra,
    });
  }
}

export function initPostHog() {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: "2026-01-30",
    capture_exceptions: true,
    // Event quota: autocapture is also off in the project settings, but this covers page loads before
    // the remote config arrives. Pageleave only fed web analytics' bounce rate and time on page.
    autocapture: false,
    capture_pageleave: false,
  });
  registerEnvironment();
}

const SENT_PERSON_KEY = "posthog_sent_person";

/**
 * identify() with properties sends a $set on every app load, even when nothing changed (posthog-js
 * only skips duplicates within one page load). So the properties go along only when they differ
 * from the last ones this browser sent for this user.
 */
export function identifyPostHogUser(userId: string, properties: Record<string, unknown>) {
  if (!isPostHogEnabled()) {
    return;
  }
  const sent = JSON.stringify([userId, properties]);
  let unchanged = false;
  try {
    unchanged = localStorage.getItem(SENT_PERSON_KEY) === sent;
  } catch {
    // No storage: send them, as before.
  }
  posthog.identify(userId, unchanged ? undefined : properties);
  if (!unchanged) {
    try {
      localStorage.setItem(SENT_PERSON_KEY, sent);
    } catch {
      // Sent again next load; no harm.
    }
  }
}

/** Company is per-session context for multi-company users, so it goes on every event rather than only the person. */
export function setPostHogCompany(company: CompanyInformation | null) {
  if (!isPostHogEnabled()) {
    return;
  }
  posthog.register({ company_id: company?.id ?? null, company_name: company?.name ?? null });
}

/** Forget the identified user. reset() also clears registered properties, so environment is put back. */
export function resetPostHog() {
  if (!isPostHogEnabled()) {
    return;
  }
  posthog.reset();
  registerEnvironment();
}

function registerEnvironment() {
  // One project for all environments; filter insights on this property
  posthog.register({ environment: configValue("VUE_APP_ENVIRONMENT") });
}

const kaiViewedWorkOrders = new Set<string>();

/**
 * Also the project's session replay event trigger, so only sessions where kAI suggestions were on
 * screen get recorded. Sent once per work order per page load.
 */
export function trackKaiSuggestionsViewed(workOrderId: string) {
  if (!isPostHogEnabled() || kaiViewedWorkOrders.has(workOrderId)) {
    return;
  }
  kaiViewedWorkOrders.add(workOrderId);
  posthog.capture("kAI Suggestions Viewed", { workOrderId });
}

export function showExpenseFeedbackSurvey() {
  showCentredSurvey(EXPENSE_FEEDBACK_SURVEY_V2_ID);
}

export function showKaiFeedbackSurvey() {
  showCentredSurvey(KAI_FEEDBACK_SURVEY_ID);
}

export function showDraftFeedbackSurvey() {
  showCentredSurvey(DRAFT_FEEDBACK_SURVEY_ID);
}

export function showExpenseNewsSurvey() {
  return showCentredSurvey(EXPENSE_NEWS_SURVEY_ID);
}

// Reka gives popovers role="dialog" too; they sit in a popper wrapper, which modals and drawers don't.
export function isDialogOpen() {
  return [...document.querySelectorAll('[role="dialog"][data-state="open"]')].some(
    (dialog) => !dialog.closest("[data-reka-popper-content-wrapper]"),
  );
}

/**
 * Opens an API survey centred on screen. Position is set here because choosing it in the PostHog UI
 * is a paid feature. getActiveMatchingSurveys applies the survey's conditions and once-per-user
 * targeting, so users who already answered or dismissed are skipped. It doesn't open while a modal is
 * open, or after the returned cancel is called; then the survey stays unseen and comes back the next time.
 */
function showCentredSurvey(surveyId: string): () => void {
  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = () => {
    cancelled = true;
    clearTimeout(timer);
  };
  if (!isPostHogEnabled()) {
    return cancel;
  }
  posthog.getActiveMatchingSurveys((surveys) => {
    const survey = surveys.find((s) => s.id === surveyId);
    if (cancelled || !survey) {
      return;
    }
    // we run the popup delay ourselves. With ignoreDelay: false the SDK's auto-display loop
    // cancels the pending timer within ~1s, because it never auto-shows API surveys (posthog-js 1.435.2).
    const delayMs = (survey.appearance?.surveyPopupDelaySeconds ?? 0) * 1000;
    timer = setTimeout(() => {
      if (isDialogOpen()) {
        return;
      }
      posthog.displaySurvey(surveyId, {
        displayType: DisplaySurveyType.Popover,
        position: SurveyPosition.MiddleCenter,
        ignoreConditions: false,
        ignoreDelay: true,
      });
    }, delayMs);
  });
  return cancel;
}
